import React, { useState, useMemo } from 'react';
import colors from '../data/colors.json';
import './ColorSelector.css';

export default function ColorSelector({
    value,
    onChange,
    label = "Select Color",
    required = false,
    compact = false  // New prop for compact mode
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    // Filter colors based on search term
    const filteredColors = useMemo(() => {
        if (!searchTerm) return colors;

        const term = searchTerm.toLowerCase();
        return colors.filter(color =>
            color.name.toLowerCase().includes(term) ||
            color.code.toLowerCase().includes(term)
        );
    }, [searchTerm]);

    // Popular exterior colors (used for main + compact quick picks)
    const popularExteriorColors = useMemo(() => {
        const popularCodes = [
            'SW7006', // Extra White
            'SW7015', // Repose Gray
            'SW7005', // Pure White
            'SW6204', // Sea Salt
            'SW7012', // Creamy
            'SW7008', // Alabaster
            'SW7036', // Accessible Beige
            'SW6258', // Tricorn Black
            'SW7657', // Slate Tile
            'SW7043', // Worldly Gray
            'SW6244', // Naval
            'SW7024', // Functional Gray
            'SW7029', // Agreeable Gray
            'SW7016', // Mindful Gray
            'SW6207', // Retreat
        ];

        return colors.filter(c => popularCodes.includes(c.code));
    }, []);

    const resolvedValue = useMemo(() => {
        if (!value) return null;
        if (value.hex && value.hex !== '#CCCCCC') return value;
        if (!value.code) return value;
        const match = colors.find(color => color.code === value.code);
        return match ? { ...value, hex: match.hex } : value;
    }, [value]);

    const handleColorSelect = (color) => {
        onChange(color);
        setShowDropdown(false);
        setSearchTerm('');
    };

    // Compact mode - just search box and selected display
    if (compact) {
        const compactSwatches = popularExteriorColors.slice(0, 10);

        return (
            <div className="color-selector compact">
                <div className="compact-label-row">
                    <label className="compact-label">{label}{required && ' *'}</label>
                    <div className="compact-palette-hint" aria-hidden="true">
                        {compactSwatches.map(color => (
                            <span
                                key={color.code}
                                className="compact-palette-dot"
                                style={{ backgroundColor: color.hex }}
                            />
                        ))}
                    </div>
                </div>

                {resolvedValue ? (
                    <div className="selected-color-compact">
                        <div className="color-chip" style={{ backgroundColor: resolvedValue.hex }} />
                        <span className="color-code">{resolvedValue.code}</span>
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={() => onChange(null)}
                            title="Clear selection"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="search-container-compact">
                            <input
                                type="text"
                                className="search-input-compact"
                                placeholder="Search color..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                            />

                            {showDropdown && filteredColors.length > 0 && searchTerm && (
                                <div className="color-dropdown-compact">
                                    {filteredColors.slice(0, 20).map(color => (
                                        <div
                                            key={color.code}
                                            className="color-option-compact"
                                            onClick={() => handleColorSelect(color)}
                                        >
                                            <div className="option-chip" style={{ backgroundColor: color.hex }} />
                                            <span><strong>{color.code}</strong> - {color.name}</span>
                                        </div>
                                    ))}
                                    {filteredColors.length > 20 && (
                                        <div className="more-results-compact">
                                            + {filteredColors.length - 20} more
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Full mode - with popular colors (only for Main House Color)
    return (
        <div className="color-selector">
            <label>{label}{required && ' *'}</label>

            {/* Popular Colors Grid */}
            {!searchTerm && !value && (
                <div className="popular-colors">
                    <h4>Popular Exterior Colors</h4>
                    <div className="color-swatches">
                        {popularExteriorColors.map(color => (
                            <div
                                key={color.code}
                                className="color-swatch"
                                onClick={() => handleColorSelect(color)}
                                title={`${color.code} - ${color.name}`}
                            >
                                <div
                                    className="swatch-color"
                                    style={{ backgroundColor: color.hex }}
                                />
                                <div className="swatch-label">{color.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Selected Color Display */}
            {resolvedValue && (
                <div className="selected-color">
                    <div
                        className="color-preview"
                        style={{ backgroundColor: resolvedValue.hex }}
                    />
                    <div className="color-info">
                        <strong>{resolvedValue.code}</strong> - {resolvedValue.name}
                    </div>
                    <button
                        type="button"
                        className="change-btn"
                        onClick={() => {
                            onChange(null);
                            setShowDropdown(true);
                        }}
                    >
                        Change
                    </button>
                </div>
            )}

            {/* Search Input */}
            {!value && (
                <div className="search-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by color name or code (e.g., 'Repose Gray' or '7015')..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                    />

                    {/* Dropdown Results */}
                    {showDropdown && filteredColors.length > 0 && (
                        <div className="color-dropdown">
                            {filteredColors.slice(0, 50).map(color => (
                                <div
                                    key={color.code}
                                    className="color-option"
                                    onClick={() => handleColorSelect(color)}
                                >
                                    <div
                                        className="option-swatch"
                                        style={{ backgroundColor: color.hex }}
                                    />
                                    <div className="option-info">
                                        <strong>{color.code}</strong> - {color.name}
                                    </div>
                                </div>
                            ))}
                            {filteredColors.length > 50 && (
                                <div className="more-results">
                                    + {filteredColors.length - 50} more colors (refine your search)
                                </div>
                            )}
                        </div>
                    )}

                    {showDropdown && searchTerm && filteredColors.length === 0 && (
                        <div className="color-dropdown">
                            <div className="no-results">No colors found matching "{searchTerm}"</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
